// GPH Natural Conversation Agent — a camada que faz a Mi conversar de verdade.
//
// Regra central do produto: o usuário não usa ferramentas — ele conversa.
// A Mi entende, conduz por perguntas naturais (slot filling por texto),
// usa as skills internamente e só rende módulo quando existe resultado real.
//
// Contrato: `understand(texto, contextoDaConversa, opts)` retorna UMA decisão
// com UMA ação principal por turno (turn discipline). Nunca empilha
// chat + pricing + settings + fallback.
//
// Este módulo é puro (sem UI, rede ou estado) — GphAgentApp executa as decisões.

import { resolveIntent } from './gphIntentResolver';
import { GPH_COPY } from './gphCopy';
import { getGameLabel } from './gphGameOntology';
import { resolveGame } from './gphContextEngine';
import { checkVideoValidation } from './gphVideoValidationGuard';
import { isFreshContextEntry, normalizeAnalysisGoal } from './gphConversationStateManager';
import {
  mapSymptomToDrillGoal,
  mapObjectiveToDrillGoal,
  getObjectiveAllowedCategories,
  SYMPTOM_ALLOWED_CATEGORIES
} from './gphCoherenceGuard';
import { findDrillByGoal } from '../../data/gphDrillCatalog';

// ── Feature flag: quick actions são experiência de debug, nunca de produção ──
export const isQuickActionsEnabled = () =>
  process.env.REACT_APP_GPH_QUICK_ACTIONS_ENABLED === 'true';

// ── Turn discipline: as únicas saídas primárias permitidas ──────────────────
export const PRIMARY_OUTPUTS = [
  'chat_response',
  'ask_slot',
  'execute_skill',
  'ask_video',
  'render_result_module',
  'render_settings',
  'render_pricing',
  'render_limits',
  'render_support',
  'redirect_out_of_scope'
];

// Chokepoint central: garante que toda decisão tem exatamente uma ação válida.
export const selectSinglePrimaryOutput = (decision = {}) =>
  PRIMARY_OUTPUTS.includes(decision.nextAction) ? decision.nextAction : 'chat_response';

const MODULE_ACTIONS = [
  'execute_skill', 'render_result_module', 'render_settings',
  'render_pricing', 'render_limits', 'render_support'
];

const decide = (partial) => {
  const d = {
    intent: 'general_help_fps',
    confidence: 0.6,
    domain: 'fps_mobile',
    game: null,
    weapon: null,
    scope: null,
    baseValue: null,
    sensitivityType: null,
    symptoms: [],
    objective: null,
    missingSlots: [],
    nextAction: 'chat_response',
    skill: null,
    capabilityId: null,
    reply: null,
    mountContext: null,
    memoryPatch: null,
    pending: null,
    ...partial
  };
  d.nextAction = selectSinglePrimaryOutput(d);
  d.shouldRenderModule = MODULE_ACTIONS.includes(d.nextAction);
  d.shouldShowPricing = d.nextAction === 'render_pricing';
  d.shouldShowSettings = d.nextAction === 'render_settings';
  d.shouldShowQuickActions = false;
  return d;
};

// ── Normalização e extratores de entidade ────────────────────────────────────
const strip = (value = '') =>
  String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const WEAPONS = [
  { id: 'akm', names: ['akm'] },
  { id: 'm416', names: ['m416', 'm4a1', 'm4'] },
  { id: 'groza', names: ['groza'] },
  { id: 'dp28', names: ['dp-28', 'dp28', 'dp 28'] },
  { id: 'beryl', names: ['beryl m762', 'beryl', 'm762'] },
  { id: 'honey_badger', names: ['honey badger'] },
  { id: 'ump', names: ['ump45', 'ump'] },
  { id: 'vector', names: ['vector'] },
  { id: 'uzi', names: ['uzi'] },
  { id: 'scar', names: ['scar-l', 'scar'] },
  { id: 'famas', names: ['famas'] },
  { id: 'aug', names: ['aug'] },
  { id: 'ace32', names: ['ace32', 'ace 32'] },
  { id: 'kar98', names: ['kar98k', 'kar98', 'k98'] },
  { id: 'm24', names: ['m24'] },
  { id: 'awm', names: ['awm'] },
  { id: 'mini14', names: ['mini14', 'mini 14'] },
  { id: 'sks', names: ['sks'] },
  { id: 'slr', names: ['slr'] },
  { id: 'mk14', names: ['mk14'] },
  { id: 'mp40', names: ['mp40'] },
  { id: 'xm8', names: ['xm8'] }
];

// Ids aceitos pela engine da calculadora PUBG (validado no backend);
// outras armas caem na curva geral sem quebrar nada.
const CALC_SAFE_WEAPONS = new Set(['akm', 'm416', 'groza', 'dp28', 'beryl', 'honey_badger']);

export const extractWeapon = (normalized) => {
  for (const weapon of WEAPONS) {
    for (const name of weapon.names) {
      if (normalized.includes(name)) {
        return { id: weapon.id, label: name.toUpperCase().replace(/\s+/g, '') };
      }
    }
  }
  return null;
};

const SCOPES = [
  { key: 'red_dot', label: 'Red Dot', names: ['red dot', 'reddot', 'ponto vermelho', 'holografica', 'holo', 'colimador'] },
  { key: '2x', label: '2x', names: ['2x'] },
  { key: '3x', label: '3x', names: ['3x'] },
  { key: '4x', label: '4x', names: ['4x', 'acog'] },
  { key: '6x', label: '6x', names: ['6x'] },
  { key: '8x', label: '8x', names: ['8x'] }
];

export const extractScope = (normalized) => {
  for (const scope of SCOPES) {
    for (const name of scope.names) {
      if (normalized.includes(name)) return { key: scope.key, label: scope.label };
    }
  }
  return null;
};

// Ordem importa: compostos ("ads gyro") antes dos simples ("ads", "gyro").
const SENSI_TYPES = [
  { key: 'ads_gyro', label: 'ADS Gyro', controlMode: 'gyro', names: ['ads gyro', 'gyro ads', 'ads girosc', 'girosc ads', 'ads gyroscope'] },
  { key: 'ads_camera', label: 'ADS Câmera', controlMode: 'camera', names: ['ads camera', 'camera ads'] },
  { key: 'gyro', label: 'Giroscópio', controlMode: 'gyro', names: ['giroscopio', 'gyro'] },
  { key: 'camera', label: 'Câmera', controlMode: 'camera', names: ['camera'] },
  { key: 'ads', label: 'ADS', controlMode: 'camera', names: ['ads'] },
  { key: 'geral', label: 'Geral', controlMode: 'camera', names: ['sensibilidade geral', 'geral'] }
];

export const extractSensitivityType = (normalized) => {
  for (const type of SENSI_TYPES) {
    for (const name of type.names) {
      if (normalized.includes(name)) return { key: type.key, label: type.label, controlMode: type.controlMode };
    }
  }
  return null;
};

// Valor numérico "limpo": ignora dígitos colados em letras (m416, 4x, 1v4).
export const extractBaseValue = (normalized) => {
  const cleaned = normalized.replace(/\b(?=\w*\d)(?=\w*[a-z])\w+\b/g, ' ');
  const match = cleaned.match(/\b(\d{1,3}(?:[.,]\d{1,2})?)\b/);
  if (!match) return null;
  const value = Number(match[1].replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
};

// Sintomas de mira — código externo (spec) + chave interna do diagnóstico.
const SYMPTOMS = [
  { code: 'aim_rises', key: 'sobe', hint: 'excesso de subida no recoil', names: ['sobe', 'subindo', 'levanta', 'pra cima', 'para cima'] },
  { code: 'aim_drops', key: 'cai', hint: 'sensibilidade vertical alta demais', names: ['desce', 'descendo', 'cai', 'caindo', 'pra baixo', 'para baixo'] },
  { code: 'aim_shakes', key: 'treme', hint: 'instabilidade horizontal', names: ['treme', 'tremendo', 'tremida', 'balanca', 'abre demais', 'abrindo'] },
  { code: 'aim_overshoots', key: 'passa', hint: 'transição rápida demais', names: ['passa do alvo', 'passa reto', 'passa direto', 'passando do alvo'] },
  { code: 'cant_track', key: 'nao_acompanha', hint: 'tracking pesado', names: ['nao acompanha', 'nao acompanho', 'nao consigo acompanhar', 'perco o alvo', 'perde o alvo'] }
];

export const extractSymptom = (normalized) => {
  for (const symptom of SYMPTOMS) {
    for (const name of symptom.names) {
      if (normalized.includes(name)) return symptom;
    }
  }
  return null;
};

// Resposta de giroscópio. Palavras soltas ("full", "sim") só contam quando a
// pergunta ativa é sobre gyro — fora disso exigem menção explícita a gyro.
export const parseGyroAnswer = (normalized, { bareAllowed = false } = {}) => {
  const mentionsGyro = /(gyro|girosc)/.test(normalized);
  if (!mentionsGyro && !bareAllowed) return null;
  if (/(full|total|sempre|no maximo)/.test(normalized)) return 'gyro_full';
  if (/(parcial|so no ads|apenas (no )?ads|scope on)/.test(normalized)) return 'gyro_partial';
  if (/(desligado|nao uso|sem gyro|nao jogo com|\bnao\b|\boff\b)/.test(normalized)) return 'no_gyro';
  if (/(ligado|\bsim\b|\buso\b|\bon\b)/.test(normalized)) return 'gyro_full';
  return null;
};

// ── Padrões conversacionais ──────────────────────────────────────────────────
const IDENTITY_PATTERN = /(quem (e|eh) (voce|vc|tu)\??|calma[,.!\s]*quem|quem esta falando|quem ta falando|quem ta ai|o que (voce|vc) faz|o que (voce|vc) sabe fazer|como (voce|vc) funciona|voce e (um|uma) (bot|robo|ia)|que ia e (voce|vc))/;

const HELP_PATTERN = /^(me ajuda(r)?[!.?\s]*|ajuda[!.?\s]*|preciso de ajuda[!.?\s]*|nao sei por onde come(c|ç)ar[!.?\s]*|por onde come(c|ç)o[!.?\s]*)$/;

const WELLBEING_PATTERN = /^(tudo bem|td bem|como vai|beleza|suave|blz|de boa)[!.?\s]*$/;

const ABUSIVE_OR_NOISE_PATTERN = /\b(idiota|burro|otario|otária|otaria|lixo|fdp|vai se foder|vtnc|babaca)\b/;

const VAGUE_AIM_HELP_PATTERN = /\b(me ajuda|me ajude|ajuda|preciso de ajuda|quero ajuda)\b[\s\S]{0,36}\b(mira|aim)\b|\b(mira|aim)\b[\s\S]{0,24}\b(me ajuda|me ajude|ajuda)\b/;

// Fora de domínio: só redireciona quando o assunto é claramente não-FPS.
const OFF_DOMAIN_PATTERN = /\b(politica|politico|eleicao|presidente|futebol|namor(o|ada|ado)|crush|receita|cozinhar|bolo|filme|serie|novela|criptomoeda|bitcoin|investimento|acoes da bolsa|emprestimo|financas|dinheiro emprestado|religiao|igreja|historia do brasil|matematica|programacao|programar|codigo fonte|javascript|python|escola|faculdade|prova de|licao de casa|saude|remedio|medico|dieta|academia de musculacao|noticias|horoscopo|signo)\b/;

const FPS_DOMAIN_PATTERN = /(mira|sensi|sensibilidade|spray|recoil|gyro|girosc|trein(o|ar|a)|x1|1v\d|rush|clutch|capa|headshot|hs\b|scope|ads\b|gameplay|video|clipe|replay|pubg|free fire|freefire|codm|cod mobile|arma|rank|partida|squad|jog(o|ar|ando)|fps|granada|drop|loot|bot\b|lag)/;

const PAIN_PATTERN = /(mira|sensi|aim)[\s\S]{0,32}(uma merda|ruim|horrivel|pessima|lixo|bosta|zoada|estranha|desandou|piorou)|(ta|tá|esta|anda) (uma )?(merda|ruim|horrivel)[\s\S]{0,20}(mira|sensi)/;

const LOSING_X1_PATTERN = /(perdendo|perco|sempre perco|vivo perdendo)[\s\S]{0,16}x1|x1[\s\S]{0,16}(perdendo|perco)/;

const VIDEO_REQUEST_PATTERN = /\b(analis\w*|avali\w*|olha|ve como)\b[\s\S]{0,28}\b(video|clipe|gameplay|replay|lance)\b|\b(video|clipe)\b[\s\S]{0,28}\b(analis\w*|avali\w*)\b/;

const CAPA_PATTERN = /(puxar|puxo|acertar|acerto)[\s\S]{0,12}(capa|cabeca|headshot)|nao consigo (dar )?(capa|headshot)/;

// Pedidos explícitos têm prioridade sobre slot filling pendente
// ("quero assinar pro" no meio de um diagnóstico troca de assunto).
const EXPLICIT_REQUEST_PATTERN = /\b(quero|queria|preciso|bora|vamos|me (mostra|mostre|fala|ajuda|explica|manda|diz)|calcul(a|ar|e)|analis(a|ar|e)|assinar|comprar|cancelar|configurac|trein(ar|a)\b|valida(r)?\b|quem (e|eh)|o que (voce|vc))\b/;

const symptomHint = (symptom) => (symptom ? `Isso parece ${symptom.hint}.` : 'Entendi.');

const compactComparable = (value = '') => strip(value).replace(/\s+/g, ' ').trim();

const isRepeatedTurn = (ctx = {}, rawText = '') => (
  Boolean(ctx?.lastUserText)
  && compactComparable(ctx.lastUserText) === compactComparable(rawText)
);

const pickConversationalReply = (ctx = {}, rawText = '', primary, repeat) => (
  isRepeatedTurn(ctx, rawText) ? repeat : primary
);

// ── Perguntas naturais de slot (sempre texto, nunca botão) ───────────────────
const QUESTIONS = {
  symptom:
    'Entendi. Quando você sente isso, o problema é mais no spray (mira subindo), na mira caindo, tremendo para os lados ou em acompanhar o inimigo?',
  symptomRetry:
    'Me diz assim, do seu jeito: ela sobe, cai, treme ou passa do alvo?',
  gyroMode: (symptom) =>
    `${symptomHint(symptom)} Para eu te orientar certo: você joga com giroscópio ligado, parcial ou desligado?`,
  weaponScope:
    'Fechado. Agora me diga a arma e a mira em que isso mais acontece. Exemplo: M416 Red Dot ou AKM 4x.',
  calculatorAll: (weaponLabel) =>
    `Consigo.${weaponLabel ? ` Para calcular a ${weaponLabel},` : ''} me diga a mira base, o valor e o tipo de sensibilidade.\n\nExemplo: Red Dot 115 ADS Gyro.`,
  baseValue: (scopeLabel) =>
    `Só falta o valor da sua ${scopeLabel || 'mira base'}. Exemplo: 115.`,
  baseScope:
    'Só falta a mira base. Exemplo: Red Dot, 2x ou 3x.',
  sensitivityType:
    'Só falta o tipo de sensibilidade: ADS Gyro, ADS Câmera, Giroscópio ou Câmera?',
  drillGoal:
    'Fechado. Qual situação você quer treinar? Pode ser rush, x1 dentro de casa, strafe, defesa de pinçamento ou outra jogada.',
  x1Context:
    'Fechado. Esse x1 que você perde é mais de perto, dentro de casa, ou em campo aberto?',
  videoGoal:
    'Recebi o vídeo. O que você quer que eu avalie nele: mira, spray, movimentação, x1 ou análise geral?',
  videoGoalRetry:
    'Me diga em uma palavra o foco: mira, movimentação, x1 ou análise geral?',
  rawValueChoice: (scopeLabel, value) =>
    `Entendi, sua ${scopeLabel} está em ${value}. Você quer calcular as outras miras a partir dela, ou ajustar porque a mira está ruim?`
};

const pendingOf = (skill, missingSlots, slots) => ({ skill, missingSlots, slots });

// ── Fluxo: sensibilidade (dor de mira → diagnóstico) ─────────────────────────
const SENSITIVITY_ORDER = ['symptom', 'gyroMode', 'weaponScope'];

const sensitivityMissing = (slots) => SENSITIVITY_ORDER.filter((slot) => {
  if (slot === 'symptom') return !slots.symptom;
  if (slot === 'gyroMode') return !slots.gyroMode;
  return !slots.weapon && !slots.scope;
});

const sensitivityStep = (slots, { game, intent = 'sensitivity_diagnosis', confidence = 0.9 } = {}) => {
  const missing = sensitivityMissing(slots);
  const symptomEntry = SYMPTOMS.find((s) => s.key === slots.symptom) || null;

  if (missing.length === 0) {
    return decide({
      intent: 'sensitivity_diagnosis',
      confidence,
      skill: 'sensitivity',
      game,
      weapon: slots.weaponLabel || null,
      scope: slots.scopeLabel || null,
      symptoms: symptomEntry ? [symptomEntry.code] : [],
      objective: 'recoil_control',
      nextAction: 'execute_skill',
      capabilityId: 'sensitivity_diagnostic',
      mountContext: {
        symptom: slots.symptom,
        gyroMode: slots.gyroMode || null,
        weapon: slots.weapon || null,
        scope: slots.scope || null,
        game
      },
      memoryPatch: slots.gyroMode ? { controlMethod: slots.gyroMode } : null,
      reply: 'Fechado. Com isso eu já consigo montar seu diagnóstico.',
      pending: null
    });
  }

  const nextSlot = missing[0];
  let question = QUESTIONS.symptom;
  if (nextSlot === 'gyroMode') question = QUESTIONS.gyroMode(symptomEntry);
  if (nextSlot === 'weaponScope') question = QUESTIONS.weaponScope;

  return decide({
    intent,
    confidence,
    skill: 'sensitivity',
    game,
    weapon: slots.weaponLabel || null,
    scope: slots.scopeLabel || null,
    symptoms: symptomEntry ? [symptomEntry.code] : [],
    objective: 'recoil_control',
    missingSlots: missing,
    nextAction: 'ask_slot',
    reply: question,
    pending: pendingOf('sensitivity', missing, slots)
  });
};

// ── Fluxo: calculadora (miras a partir de uma base) ──────────────────────────
const CALCULATOR_ORDER = ['baseScope', 'baseValue', 'sensitivityType'];

const calculatorMissing = (slots) => CALCULATOR_ORDER.filter((slot) => {
  if (slot === 'baseScope') return !slots.scope;
  if (slot === 'baseValue') return typeof slots.baseValue !== 'number';
  return !slots.sensitivityType;
});

const calculatorStep = (slots, { game } = {}) => {
  if (game === 'codm') {
    return decide({
      intent: 'calculator_request',
      confidence: 1,
      skill: 'calculator',
      game,
      nextAction: 'chat_response',
      reply: GPH_COPY.codmUnavailable,
      pending: null
    });
  }

  const missing = calculatorMissing(slots);

  if (missing.length === 0) {
    const capabilityId = game === 'free_fire' ? 'calculator_freefire' : 'calculator_pubg';
    const weaponSafe = slots.weapon && CALC_SAFE_WEAPONS.has(slots.weapon) ? slots.weapon : null;
    return decide({
      intent: 'calculator_request',
      confidence: 0.95,
      skill: 'calculator',
      game,
      weapon: slots.weaponLabel || null,
      scope: slots.scopeLabel || null,
      baseValue: slots.baseValue,
      sensitivityType: slots.sensitivityTypeLabel || null,
      nextAction: 'execute_skill',
      capabilityId,
      mountContext: {
        game,
        baseValue: slots.baseValue,
        scope: slots.scope,
        weapon: weaponSafe,
        controlMode: slots.controlMode || 'gyro'
      },
      reply: `Fechado. Calculei as miras${slots.weaponLabel ? ` da ${slots.weaponLabel}` : ''} com base em ${slots.scopeLabel} ${slots.sensitivityTypeLabel || ''} ${slots.baseValue}.`.replace(/\s+/g, ' '),
      pending: null
    });
  }

  let question;
  if (missing.length >= 2) question = QUESTIONS.calculatorAll(slots.weaponLabel);
  else if (missing[0] === 'baseValue') question = QUESTIONS.baseValue(slots.scopeLabel);
  else if (missing[0] === 'baseScope') question = QUESTIONS.baseScope;
  else question = QUESTIONS.sensitivityType;

  return decide({
    intent: 'calculator_request',
    confidence: 0.9,
    skill: 'calculator',
    game,
    weapon: slots.weaponLabel || null,
    scope: slots.scopeLabel || null,
    baseValue: typeof slots.baseValue === 'number' ? slots.baseValue : null,
    sensitivityType: slots.sensitivityTypeLabel || null,
    missingSlots: missing,
    nextAction: 'ask_slot',
    reply: question,
    pending: pendingOf('calculator', missing, slots)
  });
};

// ── Fluxo: treino ────────────────────────────────────────────────────────────
const trainingExecute = (normalized, rawText, { game, objective = null } = {}) => decide({
  intent: 'training_request',
  confidence: 0.9,
  skill: 'training',
  game,
  objective: objective || mapObjectiveToDrillGoal(normalized),
  nextAction: 'execute_skill',
  capabilityId: 'training_single_drill',
  mountContext: {
    drillGoal: mapObjectiveToDrillGoal(normalized),
    rawText,
    allowedCategories: getObjectiveAllowedCategories(normalized),
    game
  },
  reply: null,
  pending: null
});

// ── Fluxo: análise de vídeo ──────────────────────────────────────────────────
const VIDEO_GOAL_MAP = [
  { capabilityId: 'mi_analysis_aim', label: 'mira e sensibilidade', pattern: /(mira|sensi|spray|recoil)/ },
  { capabilityId: 'mi_analysis_movement', label: 'movimentação e posicionamento', pattern: /(moviment|posicion)/ },
  { capabilityId: 'mi_analysis_x1', label: 'o x1', pattern: /(x1|duelo|troca)/ },
  { capabilityId: 'mi_analysis_gameplay', label: 'a gameplay como um todo', pattern: /(geral|tudo|completo|decis|jogo todo|gameplay)/ }
];

const matchVideoGoal = (normalized) =>
  VIDEO_GOAL_MAP.find((goal) => goal.pattern.test(normalized)) || null;

const videoGoalExecute = (goal, { game, hasVideo }) => decide({
  intent: 'video_analysis_request',
  confidence: 0.9,
  skill: 'video_analysis',
  game,
  objective: goal.label,
  nextAction: 'execute_skill',
  capabilityId: goal.capabilityId,
  mountContext: { hasVideo: Boolean(hasVideo), game },
  reply: `Fechado. Vou avaliar ${goal.label} no seu vídeo.`,
  pending: null
});

// ── Continuação de slot filling (a Mi lembra o que perguntou) ────────────────
const continuePending = (normalized, rawText, ctx, { hasVideo, game }) => {
  const skill = ctx.pendingSkill;
  const slots = { ...(ctx.slots || {}) };
  const currentSlot = (ctx.pendingSlots || [])[0] || null;
  let filled = 0;

  if (skill === 'sensitivity') {
    const symptom = extractSymptom(normalized);
    if (symptom && !slots.symptom) { slots.symptom = symptom.key; filled += 1; }
    const weapon = extractWeapon(normalized);
    if (weapon && !slots.weapon) { slots.weapon = weapon.id; slots.weaponLabel = weapon.label; filled += 1; }
    const scope = extractScope(normalized);
    if (scope && !slots.scope) { slots.scope = scope.key; slots.scopeLabel = scope.label; filled += 1; }
    const gyro = parseGyroAnswer(normalized, { bareAllowed: currentSlot === 'gyroMode' });
    if (gyro && !slots.gyroMode) { slots.gyroMode = gyro; filled += 1; }

    if (filled === 0) {
      const attempts = (slots.attempts || 0) + 1;
      if (currentSlot === 'symptom' && attempts >= 2) {
        // Duas tentativas sem sintoma claro → diagnóstico geral honesto.
        return { filled: 1, decision: sensitivityStep({ ...slots, symptom: 'nao_sei', attempts }, { game }) };
      }
      const retry = currentSlot === 'symptom' ? QUESTIONS.symptomRetry
        : currentSlot === 'gyroMode' ? 'Só me confirma o giroscópio: ligado, parcial ou desligado?'
          : QUESTIONS.weaponScope;
      return {
        filled: 0,
        decision: decide({
          intent: 'sensitivity_diagnosis',
          skill: 'sensitivity',
          game,
          missingSlots: ctx.pendingSlots || [],
          nextAction: 'ask_slot',
          reply: retry,
          pending: pendingOf('sensitivity', ctx.pendingSlots || [], { ...slots, attempts })
        })
      };
    }
    return { filled, decision: sensitivityStep({ ...slots, attempts: 0 }, { game }) };
  }

  if (skill === 'calculator') {
    const scope = extractScope(normalized);
    if (scope && !slots.scope) { slots.scope = scope.key; slots.scopeLabel = scope.label; filled += 1; }
    const value = extractBaseValue(normalized);
    if (value !== null && typeof slots.baseValue !== 'number') { slots.baseValue = value; filled += 1; }
    const type = extractSensitivityType(normalized);
    if (type && !slots.sensitivityType) {
      slots.sensitivityType = type.key;
      slots.sensitivityTypeLabel = type.label;
      slots.controlMode = type.controlMode;
      filled += 1;
    }
    const weapon = extractWeapon(normalized);
    if (weapon && !slots.weapon) { slots.weapon = weapon.id; slots.weaponLabel = weapon.label; filled += 1; }

    if (filled === 0) {
      return {
        filled: 0,
        decision: decide({
          intent: 'calculator_request',
          skill: 'calculator',
          game,
          missingSlots: ctx.pendingSlots || [],
          nextAction: 'ask_slot',
          reply: QUESTIONS.calculatorAll(slots.weaponLabel),
          pending: pendingOf('calculator', ctx.pendingSlots || [], slots)
        })
      };
    }
    return { filled, decision: calculatorStep(slots, { game }) };
  }

  if (skill === 'training') {
    const base = slots.objectiveBase ? `${slots.objectiveBase} ${normalized}` : normalized;
    return { filled: 1, decision: trainingExecute(base, rawText, { game, objective: slots.objective || null }) };
  }

  if (skill === 'video_goal') {
    const goal = matchVideoGoal(normalized);
    if (!goal) {
      return {
        filled: 0,
        decision: decide({
          intent: 'video_analysis_request',
          skill: 'video_analysis',
          game,
          missingSlots: ['analysisGoal'],
          nextAction: 'ask_slot',
          reply: QUESTIONS.videoGoalRetry,
          pending: pendingOf('video_goal', ['analysisGoal'], slots)
        })
      };
    }
    return { filled: 1, decision: videoGoalExecute(goal, { game, hasVideo: slots.hasVideo || hasVideo }) };
  }

  if (skill === 'video_analysis') {
    // Ainda sem vídeo: guarda o foco se ele apareceu e reforça o pedido.
    const goal = matchVideoGoal(normalized);
    if (goal) slots.analysisGoal = goal.capabilityId;
    return {
      filled: goal ? 1 : 0,
      decision: decide({
        intent: 'video_analysis_request',
        skill: 'video_analysis',
        game,
        nextAction: 'ask_video',
        reply: GPH_COPY.videoAskUpload,
        pending: pendingOf('video_analysis', ['video'], slots)
      })
    };
  }

  if (skill === 'raw_choice') {
    if (/(calcul|outras|converter|conversao)/.test(normalized)) {
      return { filled: 1, decision: calculatorStep({ ...slots }, { game }) };
    }
    if (/(ruim|ajust|diagnos|corrig|errad|arruma)/.test(normalized)) {
      return { filled: 1, decision: sensitivityStep({ scope: slots.scope, scopeLabel: slots.scopeLabel }, { game, intent: 'sensitivity_diagnosis' }) };
    }
    return {
      filled: 0,
      decision: decide({
        intent: 'calculator_request',
        game,
        nextAction: 'ask_slot',
        missingSlots: ['desiredAction'],
        reply: 'Me diz só isso: você quer calcular as outras miras, ou ajustar porque ela está ruim?',
        pending: pendingOf('raw_choice', ['desiredAction'], slots)
      })
    };
  }

  return null;
};

// ── Detecção de intenção nova (mensagem fresca ou troca de assunto) ──────────
const detect = (normalized, rawText, ctx, { hasVideo, game }) => {
  const resolution = resolveIntent(rawText, { hasVideoAttachment: hasVideo });
  const mentionsFps = FPS_DOMAIN_PATTERN.test(normalized);

  // Conversa pura ─ sem skill, sem módulo, sem pricing.
  if (resolution.intent === 'greeting') {
    const savedGame = ctx?.playerMemory?.mainGame || null;
    const reply = WELLBEING_PATTERN.test(normalized)
      ? pickConversationalReply(
        ctx,
        rawText,
        'Tudo certo. Me diz onde voce quer evoluir no jogo hoje.',
        'Tudo certo por aqui. Vamos direto ao jogo: mira, sensibilidade, treino ou video?'
      )
      : pickConversationalReply(
        ctx,
        rawText,
        savedGame
          ? GPH_COPY.greetingWithGame(getGameLabel(savedGame))
          : 'Oi. Me fala o que voce quer melhorar no jogo hoje: mira, sensibilidade, treino ou analise de gameplay.',
        'Oi de novo. Me manda o problema no jogo em uma frase: mira, spray, sensibilidade, treino ou video.'
      );
    return decide({
      intent: 'greeting',
      confidence: 1,
      game: savedGame,
      nextAction: 'chat_response',
      reply,
      pending: null
    });
  }

  if (ABUSIVE_OR_NOISE_PATTERN.test(normalized) && !mentionsFps) {
    return decide({
      intent: 'abusive_or_noise',
      confidence: 1,
      game,
      nextAction: 'chat_response',
      reply: pickConversationalReply(
        ctx,
        rawText,
        'Vou manter o foco em te ajudar no jogo. Se quiser, me diga o problema: mira, spray, sensibilidade, treino ou video.',
        'Sem crise. Quando quiser ajuda real, me fala o problema no jogo: mira, spray, sensibilidade, treino ou video.'
      ),
      pending: null
    });
  }

  if (IDENTITY_PATTERN.test(normalized) || resolution.intent === 'ask_capabilities') {
    return decide({
      intent: 'identity_question',
      confidence: 1,
      game,
      nextAction: 'chat_response',
      reply: GPH_COPY.identityAnswer,
      pending: null
    });
  }

  if (HELP_PATTERN.test(normalized) || resolution.intent === 'low_intent') {
    return decide({
      intent: 'general_help_fps',
      confidence: 1,
      game,
      nextAction: 'chat_response',
      reply: pickConversationalReply(
        ctx,
        rawText,
        'Beleza. Me diz onde voce quer evoluir no jogo hoje: mira, sensibilidade, treino ou analise de gameplay.',
        'Vamos por partes: escolha um foco agora - mira, sensibilidade, treino ou video.'
      ),
      pending: null
    });
  }

  if (resolution.intent === 'out_of_scope' || (OFF_DOMAIN_PATTERN.test(normalized) && !mentionsFps)) {
    return decide({
      intent: 'out_of_scope',
      confidence: 1,
      domain: 'off_domain',
      nextAction: 'redirect_out_of_scope',
      reply: GPH_COPY.outOfScopeStrict,
      pending: null
    });
  }

  if (resolution.intent === 'set_game_preference') {
    return decide({
      intent: 'set_game_preference',
      confidence: 0.95,
      game: resolution.extractedEntities.game,
      nextAction: 'chat_response',
      memoryPatch: { mainGame: resolution.extractedEntities.game },
      reply: GPH_COPY.gamePreferenceSet(getGameLabel(resolution.extractedEntities.game)),
      pending: null
    });
  }

  if (resolution.intent === 'memory_statement') {
    const patch = { ...(resolution.extractedEntities.memory || {}) };
    if (resolution.extractedEntities.game) patch.mainGame = resolution.extractedEntities.game;
    return decide({
      intent: 'memory_statement',
      confidence: 0.9,
      game,
      nextAction: 'chat_response',
      memoryPatch: patch,
      reply: GPH_COPY.memorySaved,
      pending: null
    });
  }

  // Ações contextuais: usam o último resultado, nunca inventam contexto.
  if (resolution.intent === 'training_from_context') {
    const diagnosis = ctx?.lastDiagnosis;
    if (diagnosis?.symptom) {
      return decide({
        intent: 'training_request',
        confidence: 0.95,
        skill: 'training',
        game,
        objective: mapSymptomToDrillGoal(diagnosis.symptom),
        nextAction: 'execute_skill',
        capabilityId: 'training_single_drill',
        mountContext: {
          drillGoal: mapSymptomToDrillGoal(diagnosis.symptom),
          allowedCategories: SYMPTOM_ALLOWED_CATEGORIES[diagnosis.symptom] || null,
          fromDiagnosis: diagnosis.symptom,
          game
        },
        pending: null
      });
    }
    return decide({
      intent: 'training_request',
      confidence: 0.8,
      skill: 'training',
      game,
      missingSlots: ['drillGoal'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.drillGoal,
      pending: pendingOf('training', ['drillGoal'], {})
    });
  }

  if (resolution.intent === 'video_validation_request') {
    const check = checkVideoValidation(ctx, hasVideo);
    if (!check.canValidate) {
      return decide({
        intent: 'video_validation_request',
        confidence: 0.95,
        skill: 'video_analysis',
        game,
        nextAction: 'ask_video',
        reply: GPH_COPY.videoRequired,
        pending: null
      });
    }
    // Validação contextual só usa diagnóstico/treino DESTA conversa —
    // resultado reidratado de sessão antiga nunca vira objetivo do vídeo.
    const linkedEntry = check.linkedTo?.type === 'diagnosis' ? ctx?.lastDiagnosis : ctx?.lastTraining;
    if (check.capabilityId && !isFreshContextEntry(linkedEntry, ctx)) {
      return decide({
        intent: 'video_validation_request',
        confidence: 0.8,
        skill: 'video_analysis',
        game,
        missingSlots: ['analysisGoal'],
        nextAction: 'ask_slot',
        reply: QUESTIONS.videoGoalRetry,
        pending: pendingOf('video_goal', ['analysisGoal'], { hasVideo })
      });
    }
    if (check.capabilityId) {
      return decide({
        intent: 'video_validation_request',
        confidence: 0.95,
        skill: 'video_analysis',
        game,
        nextAction: 'execute_skill',
        capabilityId: check.capabilityId,
        mountContext: { hasVideo: true, validationGoal: check.validationGoal, linkedTo: check.linkedTo, game },
        pending: null
      });
    }
    return decide({
      intent: 'video_validation_request',
      confidence: 0.8,
      skill: 'video_analysis',
      game,
      missingSlots: ['analysisGoal'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.videoGoalRetry,
      pending: pendingOf('video_goal', ['analysisGoal'], { hasVideo })
    });
  }

  const weapon = extractWeapon(normalized);
  const scope = extractScope(normalized);
  const symptom = extractSymptom(normalized);
  const sensiType = extractSensitivityType(normalized);
  const baseValue = extractBaseValue(normalized);

  if (VAGUE_AIM_HELP_PATTERN.test(normalized) && !symptom) {
    return decide({
      intent: 'vague_aim_help',
      confidence: 0.9,
      skill: 'sensitivity',
      game,
      missingSlots: ['game', 'symptom'],
      nextAction: 'ask_slot',
      reply: pickConversationalReply(
        ctx,
        rawText,
        'Beleza. Qual jogo voce esta jogando e o que acontece com a mira: sobe, desce, treme, passa do alvo ou nao gruda no inimigo?',
        'Me manda so dois dados: o jogo e o sintoma da mira - sobe, desce, treme, passa do alvo ou nao gruda.'
      ),
      pending: pendingOf('sensitivity', ['symptom'], {})
    });
  }

  // Calculadora — pedido explícito de cálculo.
  if (/\bcalcul/.test(normalized)) {
    const slots = {};
    if (weapon) { slots.weapon = weapon.id; slots.weaponLabel = weapon.label; }
    if (scope) { slots.scope = scope.key; slots.scopeLabel = scope.label; }
    if (typeof baseValue === 'number') slots.baseValue = baseValue;
    if (sensiType) {
      slots.sensitivityType = sensiType.key;
      slots.sensitivityTypeLabel = sensiType.label;
      slots.controlMode = sensiType.controlMode;
    }
    return calculatorStep(slots, { game });
  }

  // Valor bruto de configuração sem objetivo ("minha red dot tá 215").
  if (resolution.intent === 'raw_config_value' && scope && typeof baseValue === 'number') {
    return decide({
      intent: 'calculator_request',
      confidence: 0.85,
      game,
      scope: scope.label,
      baseValue,
      missingSlots: ['desiredAction'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.rawValueChoice(scope.label, baseValue),
      pending: pendingOf('raw_choice', ['desiredAction'], {
        scope: scope.key, scopeLabel: scope.label, baseValue
      })
    });
  }

  // "tô perdendo x1" → dor de combate → treino conduzido por pergunta.
  if (LOSING_X1_PATTERN.test(normalized)) {
    return decide({
      intent: 'training_request',
      confidence: 0.9,
      skill: 'training',
      game,
      objective: 'close_range_duel',
      missingSlots: ['duelContext'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.x1Context,
      pending: pendingOf('training', ['duelContext'], { objectiveBase: 'x1', objective: 'close_range_duel' })
    });
  }

  // Dor de mira/sensibilidade — com ou sem sintoma claro.
  const aimContext = /(mira|sensi|spray|recoil|red dot|holo|\dx\b|scope|acog)/.test(normalized);
  const painReport = PAIN_PATTERN.test(normalized);
  if (painReport || (symptom && aimContext) || resolution.capabilityId === 'sensitivity_diagnostic' || CAPA_PATTERN.test(normalized)) {
    const slots = {};
    if (symptom) slots.symptom = symptom.key;
    if (weapon) { slots.weapon = weapon.id; slots.weaponLabel = weapon.label; }
    if (scope) { slots.scope = scope.key; slots.scopeLabel = scope.label; }
    const gyro = parseGyroAnswer(normalized);
    if (gyro) slots.gyroMode = gyro;
    const intent = symptom ? 'sensitivity_diagnosis' : 'pain_report';
    return sensitivityStep(slots, { game, intent, confidence: symptom ? 0.92 : 0.8 });
  }

  // Treino.
  if (resolution.capabilityId === 'training_single_drill' || /\btrein(ar|o|a)\b/.test(normalized)) {
    if (findDrillByGoal(normalized)) {
      return trainingExecute(normalized, rawText, { game });
    }
    return decide({
      intent: 'training_request',
      confidence: 0.85,
      skill: 'training',
      game,
      missingSlots: ['drillGoal'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.drillGoal,
      pending: pendingOf('training', ['drillGoal'], {})
    });
  }

  // Análise de vídeo pedida por texto.
  if (VIDEO_REQUEST_PATTERN.test(normalized) || (resolution.capabilityId || '').startsWith('mi_analysis')) {
    const goal = matchVideoGoal(normalized)
      || ((resolution.capabilityId || '').startsWith('mi_analysis')
        ? VIDEO_GOAL_MAP.find((g) => g.capabilityId === resolution.capabilityId)
        : null);
    if (hasVideo) {
      if (goal) return videoGoalExecute(goal, { game, hasVideo: true });
      return decide({
        intent: 'video_analysis_request',
        confidence: 0.9,
        skill: 'video_analysis',
        game,
        missingSlots: ['analysisGoal'],
        nextAction: 'ask_slot',
        reply: QUESTIONS.videoGoal,
        pending: pendingOf('video_goal', ['analysisGoal'], { hasVideo: true })
      });
    }
    return decide({
      intent: 'video_analysis_request',
      confidence: 0.9,
      skill: 'video_analysis',
      game,
      missingSlots: ['video'],
      nextAction: 'ask_video',
      reply: GPH_COPY.videoAskUpload,
      pending: pendingOf('video_analysis', ['video'], goal ? { analysisGoal: goal.capabilityId } : {})
    });
  }

  // Módulos de conta — só quando o usuário pede explicitamente.
  if (resolution.capabilityId === 'purchase_pro') {
    return decide({ intent: 'pricing_request', confidence: 0.95, nextAction: 'render_pricing', capabilityId: 'purchase_pro', pending: null });
  }
  if (resolution.capabilityId === 'profile_settings') {
    return decide({ intent: 'settings_request', confidence: 0.95, nextAction: 'render_settings', capabilityId: 'profile_settings', pending: null });
  }
  if (resolution.capabilityId === 'profile_plan') {
    return decide({ intent: 'limits_request', confidence: 0.95, nextAction: 'render_limits', capabilityId: 'profile_plan', pending: null });
  }
  if (resolution.capabilityId === 'support_community') {
    return decide({ intent: 'support_request', confidence: 0.95, nextAction: 'render_support', capabilityId: 'support_community', pending: null });
  }
  if (['training_favorites', 'admin_panel', 'partner_panel'].includes(resolution.capabilityId)) {
    return decide({
      intent: resolution.intent,
      confidence: 0.9,
      nextAction: 'execute_skill',
      capabilityId: resolution.capabilityId,
      mountContext: {},
      pending: null
    });
  }

  return null;
};

// ── Dica semântica da Mi Brain ───────────────────────────────────────────────
// Quando o texto cairia no bucket genérico local, a decisão do cérebro
// (backend) tem prioridade: ela diferencia small talk, ajuda vaga, ruído,
// fora de escopo e ajuda geral — cada um com resposta/pergunta contextual.
// Detecções locais concretas (settings, pricing, memória, saudação, skills)
// NUNCA são sobrepostas pela dica: ela só substitui o fallback genérico.
const HINT_INTENT_MAP = {
  small_talk: 'greeting',
  vague_help: 'general_help_fps',
  noise_or_abuse: 'abusive_or_noise',
  out_of_scope: 'out_of_scope',
  general_help: 'general_help_fps'
};

const decisionFromSemanticHint = (hint, game) => {
  if (!hint || typeof hint !== 'object') return null;
  const localIntent = HINT_INTENT_MAP[hint.intent];
  if (!localIntent) return null;

  const say = hint.nextQuestion || hint.userFacingMessage || null;
  if (!say) return null;

  return decide({
    intent: localIntent,
    confidence: Math.max(0.6, Number(hint.confidence) || 0),
    game,
    nextAction: hint.intent === 'out_of_scope' ? 'redirect_out_of_scope' : 'chat_response',
    reply: say,
    pending: null
  });
};

// ── Entrada principal ────────────────────────────────────────────────────────
export const understand = (rawText, ctx = {}, { hasVideo = false, hostGame = null, semanticHint = null } = {}) => {
  const raw = String(rawText || '').trim();
  const normalized = strip(raw);
  const game = resolveGame(ctx, extractGameEntity(normalized), hostGame);

  if (!normalized && !hasVideo) {
    return decide({ intent: 'general_help_fps', game, nextAction: 'chat_response', reply: GPH_COPY.generalHelpFps, pending: null });
  }

  // 1) Resposta a uma pergunta pendente da Mi (slot filling tem prioridade,
  //    a menos que a mensagem seja claramente um pedido novo).
  if (ctx?.pendingSkill && !EXPLICIT_REQUEST_PATTERN.test(normalized)) {
    const continued = continuePending(normalized, raw, ctx, { hasVideo, game });
    if (continued && continued.filled > 0) return continued.decision;
    // Nada preencheu: tenta intenção nova antes de re-perguntar.
    const fresh = detect(normalized, raw, ctx, { hasVideo, game });
    if (fresh) return fresh;
    if (continued) return continued.decision;
  }

  // 2) Intenção nova (ou troca explícita de assunto durante slot filling).
  const decision = detect(normalized, raw, ctx, { hasVideo, game });
  if (decision) return decision;

  // 3) Pedido novo explícito não reconhecido durante um slot filling pendente.
  if (ctx?.pendingSkill) {
    const continued = continuePending(normalized, raw, ctx, { hasVideo, game });
    if (continued) return continued.decision;
  }

  // 4) Vídeo disponível na conversa: se o texto já traz o foco ("avalie minha
  //    movimentação"), executa com esse foco; sem foco, pergunta o que avaliar.
  if (hasVideo) {
    const goal = matchVideoGoal(normalized);
    if (goal) return videoGoalExecute(goal, { game, hasVideo: true });
    return decide({
      intent: 'video_analysis_request',
      confidence: 0.8,
      skill: 'video_analysis',
      game,
      missingSlots: ['analysisGoal'],
      nextAction: 'ask_slot',
      reply: QUESTIONS.videoGoal,
      pending: pendingOf('video_goal', ['analysisGoal'], { hasVideo: true })
    });
  }

  // 5) Bucket genérico: antes de cair na frase padrão, a dica semântica da
  //    Mi Brain responde com contexto real (small talk, ajuda vaga, ruído,
  //    fora de escopo) — o fallback genérico é o último recurso, não o padrão.
  const hinted = decisionFromSemanticHint(semanticHint, game);
  if (hinted) return hinted;

  if (FPS_DOMAIN_PATTERN.test(normalized) || extractWeapon(normalized) || extractScope(normalized)) {
    return decide({
      intent: 'general_help_fps',
      confidence: 0.6,
      game,
      nextAction: 'chat_response',
      reply: GPH_COPY.fpsClarify,
      pending: null
    });
  }

  if (OFF_DOMAIN_PATTERN.test(normalized)) {
    return decide({
      intent: 'out_of_scope',
      confidence: 0.9,
      domain: 'off_domain',
      nextAction: 'redirect_out_of_scope',
      reply: GPH_COPY.outOfScopeStrict,
      pending: null
    });
  }

  return decide({
    intent: 'general_help_fps',
    confidence: 0.5,
    game,
    nextAction: 'chat_response',
    reply: GPH_COPY.fpsClarify,
    pending: null
  });
};

// Jogo citado explicitamente na mensagem (nunca mistura jogos).
const GAME_ENTITIES = [
  { game: 'pubg_mobile', names: ['pubg'] },
  { game: 'free_fire', names: ['free fire', 'freefire', ' ff '] },
  { game: 'codm', names: ['codm', 'cod mobile', 'call of duty'] }
];

function extractGameEntity(normalized) {
  const hit = GAME_ENTITIES.find((entry) => entry.names.some((name) => normalized.includes(name)));
  return hit ? hit.game : null;
}

// Decisão para vídeo recém-anexado (sem texto) — usada pelo GphAgentApp.
export const understandVideoAttachment = (ctx = {}, { hostGame = null } = {}) => {
  const game = resolveGame(ctx, null, hostGame);

  // O usuário já tinha pedido análise (pendência da Mi Brain 'mi_analysis' ou
  // do fluxo natural 'video_analysis'/'video_goal'): o vídeo pertence a ESSE
  // objetivo. Com foco conhecido, executa; sem foco, o objetivo já era
  // "analisar o vídeo" — segue como análise geral, sem re-perguntar.
  if (['mi_analysis', 'video_analysis', 'video_goal'].includes(ctx.pendingSkill)) {
    const normalizedGoal = normalizeAnalysisGoal(ctx.slots?.analysisGoal);
    const goal = VIDEO_GOAL_MAP.find((g) => (
      g.capabilityId === ctx.slots?.analysisGoal
      || normalizeAnalysisGoal(g.capabilityId) === normalizedGoal
    )) || VIDEO_GOAL_MAP.find((g) => g.capabilityId === 'mi_analysis_gameplay');
    return videoGoalExecute(goal, { game, hasVideo: true });
  }

  // Diagnóstico/treino DESTA conversa → o vídeo valida aquele contexto.
  // Resultado reidratado de sessão antiga é rebaixado: nunca vira objetivo
  // do vídeo (é daqui que vinha o "validar execução do treino: cover").
  const check = checkVideoValidation(ctx, true);
  const linkedEntry = check.linkedTo?.type === 'diagnosis' ? ctx?.lastDiagnosis : ctx?.lastTraining;
  if (check.canValidate && check.capabilityId && isFreshContextEntry(linkedEntry, ctx)) {
    return decide({
      intent: 'video_validation_request',
      confidence: 0.95,
      skill: 'video_analysis',
      game,
      nextAction: 'execute_skill',
      capabilityId: check.capabilityId,
      mountContext: { hasVideo: true, validationGoal: check.validationGoal, linkedTo: check.linkedTo, game },
      reply: null,
      pending: null
    });
  }

  // Vídeo sem contexto: perguntar o que avaliar (por texto, sem botões).
  return decide({
    intent: 'video_analysis_request',
    confidence: 0.85,
    skill: 'video_analysis',
    game,
    missingSlots: ['analysisGoal'],
    nextAction: 'ask_slot',
    reply: GPH_COPY.videoReceivedNoContext,
    pending: pendingOf('video_goal', ['analysisGoal'], { hasVideo: true })
  });
};

const naturalConversationAgent = {
  understand,
  understandVideoAttachment,
  selectSinglePrimaryOutput,
  isQuickActionsEnabled,
  PRIMARY_OUTPUTS
};

export default naturalConversationAgent;
